// ============================================================================
// UNIFIED STOCKER DASHBOARD
// ============================================================================
//
// REFACTORED 2026-08-30 (Technical_Docs/Architecture_Rebuild/
// 49_FULL_STACK_REBUILD_IMPLEMENTATION_PLAN.md, Phase 2). This file was
// 1,219 lines with data-fetching, derived-state computation, and rendering
// all mixed together -- the same anti-pattern doc 49 found and planned to
// fix. Split into:
//   - hooks/useDashboardData.js         all 7 effects + retry
//   - hooks/useDashboardTransforms.js   all 9 derived-data memos
//   - DashboardHero / DashboardLoadErrorBanner / MarketOverviewSection /
//     WatchlistEmptyState / WatchlistSection / GroupsQuickAccess /
//     PillarsIntro   presentational components, each one concern
// This file is now composition only: read Redux state, call the two hooks,
// render the pieces in order. No fetching, no memo bodies, live here.
//
// Two behavior/UX changes made alongside the refactor, not silently mixed
// into "no behavior change" mechanical extraction:
//   1. GroupsQuickAccess replaces the old always-<MyGroupsNav> slot with a
//      state-aware choice -- non-owners now see RelevantGroups (a real,
//      specific invitation) instead of the slot rendering nothing.
//   2. PillarsIntro is new: a dismissible strip naming Stocker's four
//      pillars, shown above the fold. Copy is this pass's own proposal,
//      not an already-agreed list -- see that file's own header comment.
//
// PRIOR HISTORY, preserved for context (from the pre-refactor version):
// MarketPulse, the old ConsolidatedSocialSection deferral, ContextActivityRadar,
// and the sidebar DashboardPostComposer were removed in an earlier pass --
// see git history for that reasoning if it's ever needed again. The social
// section (credibility feed / trending conversations / relevant groups) was
// re-enabled once cards + market overview were confirmed wired end-to-end.
//
// POLYGON WIRE-UP GUIDE (search "POLYGON_WIRE" in MarketOverview.jsx)
// Phase 1: MarketOverview renders with built-in mock data while externalData=null.
// Phase 2: fetchPolygonSeriesThunk -> CategoryData -> <MarketOverviewD3 data=...>.
// Phase 3: fetchTickerDetailsData + fetchFinancialsData per expanded stock.
// ============================================================================

import { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { openMobileSearch, requestFocusGlobalSearch } from "../../redux/ui/uiSlice";
import { setCurrentTimeFrame, setCurrentIndicators } from "../../redux/stockData/nasdaq/stockDataReducer";
import { fetchStockDataThunk } from "../../redux/stockData/nasdaq/stockDataActions";
import { fetchMarketOverview } from "../../redux/stockData/marketSnapshot/marketSnapshotActions";
import { fetchPostsWithFilters } from "../../redux/posts/postActions";
import StockerEyeLoader from "../../components/loader/StockerEyeLoader";
import EngagementCredibilityPanel from "../../components/Credibility/EngagementCredibility";
import ConsolidatedSocialSection from "./UnifiedDashboard/ConsolidatedSocialSection";

import { useMarketOverviewData, useIndexesExtraData } from "./UnifiedDashboard/Usemarketoverviewdata";

import { useDashboardData } from "./UnifiedDashboard/hooks/useDashboardData";
import { useDashboardTransforms } from "./UnifiedDashboard/hooks/useDashboardTransforms";

import DashboardHero from "./UnifiedDashboard/DashboardHero";
import DashboardLoadErrorBanner from "./UnifiedDashboard/DashboardLoadErrorBanner";
import PillarsIntro from "./UnifiedDashboard/PillarsIntro";
import GroupsQuickAccess from "./UnifiedDashboard/GroupsQuickAccess";
import MarketOverviewSection from "./UnifiedDashboard/MarketOverviewSection";
import WatchlistEmptyState from "./UnifiedDashboard/WatchlistEmptyState";
import WatchlistSection from "./UnifiedDashboard/WatchlistSection";

const UnifiedStockerDashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // ── Local UI state ──────────────────────────────────────────────────────
  const [expandedStock, setExpandedStock] = useState(null);
  const [feedFilter, setFeedFilter] = useState("watchlist");
  const [trendingTab, setTrendingTab] = useState("credible");
  const [, setActiveComposerStock] = useState(null); // kept: see history note above

  // ── Redux selectors ─────────────────────────────────────────────────────
  const featureFlags = useSelector((s) => s.featureFlags.flags);
  const { user, loading: profileLoading } = useSelector((s) => s.profile || {});
  const { items: watchlistItems = [], loading: watchlistLoading, error: watchlistError } =
    useSelector((s) => s.watchlist || {});
  const { watchlistSimpleCharts = {} } = useSelector((s) => s.stockData || {});
  const { batchLoading: sentimentLoading } = useSelector((s) => s.sentiment || {});
  const sentimentAggState = useSelector((s) => s.sentimentAgg || { byTicker: {} });
  const {
    batchSnapshots = [],
    batchStatus,
    status: marketOverviewStatus,
    error: marketOverviewError,
  } = useSelector((s) => s.marketSnapshot || {});
  const marketLoading = batchStatus === "loading";
  const marketOverviewLoading = marketOverviewStatus === "loading";

  const { data: proxyMarketOverviewData, isLoading: proxyMarketOverviewLoading } = useMarketOverviewData(5);
  const indexesExtraData = useIndexesExtraData(5);

  const { posts = [], loading: postsLoading } = useSelector((s) => s.posts || {});
  const { groups = [], searchResults: groupSearchResults = [] } = useSelector((s) => s.groups || {});
  const contextRadarState = useSelector((s) => s.contextRadar || { global: { data: [] }, watchlist: { data: [] }, byTicker: {} });

  const chartState = useSelector((s) => s.stockData);
  const fullChartData = chartState?.data || null;
  const chartData = fullChartData?.data || [];
  const indicators = fullChartData?.indicators || {};
  const currentTimeFrame = chartState?.currentTimeFrame;
  const currentIndicators = chartState?.currentIndicators;

  const stockerSignalState = useSelector((s) => s.stockerSignal?.byTicker || {});
  const credState = useSelector((s) => s.credibility || { byUser: {} });
  const { followersList = [] } = useSelector((s) => s.userFollow || {});
  const { filings = [] } = useSelector((s) => s.edgar || {});

  const userId = user?.id;
  const loading = profileLoading || watchlistLoading;
  const dataLoading = sentimentLoading || marketLoading || postsLoading;

  const activeSignal = expandedStock ? stockerSignalState[expandedStock]?.data : null;
  const activeSignalLoading = expandedStock ? !!stockerSignalState[expandedStock]?.loading : false;
  const activeSignalError = expandedStock ? stockerSignalState[expandedStock]?.error : null;

  const dashboardLoadErrors = [
    watchlistError && { key: "watchlist", label: t("dashboard.unifiedDashboard.errorLabels.watchlist") },
    contextRadarState?.global?.error && { key: "context", label: t("dashboard.unifiedDashboard.errorLabels.context") },
    marketOverviewError && { key: "market", label: t("dashboard.unifiedDashboard.errorLabels.market") },
  ].filter(Boolean);

  // ── Data fetching (all 7 effects) ────────────────────────────────────────
  const { retryDashboardData } = useDashboardData({
    user,
    watchlistItems,
    userId,
    featureFlags,
    trendingTab,
    expandedStock,
    currentTimeFrame,
    currentIndicators,
    filings,
  });

  // ── Derived data (all 9 memos) ───────────────────────────────────────────
  const {
    watchlistData,
    trendingConversations,
    credibilityFeed,
    relevantGroups,
    filteredTechnicalContext,
    userEngagement,
  } = useDashboardTransforms({
    watchlistItems,
    batchSnapshots,
    sentimentAggState,
    watchlistSimpleCharts,
    contextRadarState,
    posts,
    groups,
    groupSearchResults,
    filings,
    expandedStock,
    credState,
    userId,
    followersList,
  });

  // With exactly one watchlist item, there's no real "which one" choice to
  // make, so the Activity Summary panel's "Select a stock" prompt (built for
  // the multi-stock case) was just wasted screen space -- found live during
  // the 2026-09-11 design audit. Auto-open the sole item once; a ref (not a
  // state flag) keeps this from re-firing and re-expanding a card the user
  // deliberately collapsed by clicking it back to null.
  const autoSelectedSoleWatchlistItem = useRef(false);
  useEffect(() => {
    if (autoSelectedSoleWatchlistItem.current) return;
    if (expandedStock || watchlistItems.length !== 1) return;
    const soleTicker = watchlistItems[0]?.symbol;
    if (!soleTicker) return;
    autoSelectedSoleWatchlistItem.current = true;
    setExpandedStock(soleTicker);
  }, [watchlistItems, expandedStock]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleStockToggle = (ticker) => {
    const next = expandedStock === ticker ? null : ticker;
    setExpandedStock(next);
    if (next) setActiveComposerStock(next);
  };

  const refreshWatchlistPosts = useCallback(() => {
    if (!featureFlags.posts) return;
    dispatch(
      fetchPostsWithFilters({
        skip: 0,
        limit: 20,
        sort_by: trendingTab === "credible" ? "trending" : "latest",
        period: "week",
      })
    );
  }, [dispatch, featureFlags.posts, trendingTab]);

  const handleTimeFrameChange = (tf) => {
    dispatch(setCurrentTimeFrame(tf));
    if (expandedStock) {
      dispatch(fetchStockDataThunk({ symbol: expandedStock, timeFrame: tf, indicators: currentIndicators }));
    }
  };

  const handleIndicatorsChange = (inds) => {
    dispatch(setCurrentIndicators(inds));
    if (expandedStock) {
      dispatch(fetchStockDataThunk({ symbol: expandedStock, timeFrame: currentTimeFrame, indicators: inds }));
    }
  };

  const handleGlobalSearchRequest = () => {
    if (window.innerWidth < 640) {
      dispatch(openMobileSearch());
      dispatch(requestFocusGlobalSearch());
    } else {
      dispatch(requestFocusGlobalSearch());
    }
  };

  const handleMarketOverviewSubChange = useCallback(
    (cat) => {
      if (cat === "Indexes") {
        // Re-fetch to get fresh snapshot data when user manually switches
        // tabs -- the data is the same batch for all Indexes subs.
        dispatch(fetchMarketOverview());
      }
    },
    [dispatch]
  );

  // ── Render — loading gate ─────────────────────────────────────────────────
  if (loading && !user) {
    return (
      <div className="flex-1 flex items-center justify-center py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900">
        <StockerEyeLoader size={64} />
      </div>
    );
  }

  // ── Render — main ─────────────────────────────────────────────────────────
  return (
    <div className="relative w-full min-h-0">
      <div className="w-full bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900 overflow-x-hidden">
        <div className="w-full">
          <div className="max-w-[1600px] mx-auto px-2 sm:px-6 py-3 sm:py-6 text-gray-900 dark:text-slate-100 w-full">

            <DashboardLoadErrorBanner errors={dashboardLoadErrors} onRetry={retryDashboardData} />

            <DashboardHero
              user={user}
              expandedStock={expandedStock}
              watchlistCount={watchlistData.length}
              dataLoading={dataLoading}
            />

            <PillarsIntro />

            <GroupsQuickAccess groups={groups} relevantGroups={relevantGroups} currentUser={user} />

            {userEngagement.posts_7d > 0 || userEngagement.groups_active > 0 || userEngagement.watchlist_size > 0 ? (
              <div className="mb-4 sm:mb-6">
                <EngagementCredibilityPanel userEngagement={userEngagement} />
              </div>
            ) : null}

            <MarketOverviewSection
              data={proxyMarketOverviewData}
              isLoading={marketOverviewLoading || proxyMarketOverviewLoading}
              indexesExtraData={indexesExtraData}
              onSubChange={handleMarketOverviewSubChange}
            />

            {watchlistData.length === 0 && !loading && (
              <WatchlistEmptyState onAddFirstStock={handleGlobalSearchRequest} />
            )}

            {watchlistData.length > 0 && (
              <WatchlistSection
                watchlistData={watchlistData}
                dataLoading={dataLoading}
                expandedStock={expandedStock}
                onStockToggle={handleStockToggle}
                onCreatePost={refreshWatchlistPosts}
                chartData={chartData}
                indicators={indicators}
                currentTimeFrame={currentTimeFrame}
                currentIndicators={currentIndicators}
                onTimeFrameChange={handleTimeFrameChange}
                onIndicatorsChange={handleIndicatorsChange}
                contextRadarState={contextRadarState}
                stockerSignalState={stockerSignalState}
                activeSignal={activeSignal}
                activeSignalLoading={activeSignalLoading}
                activeSignalError={activeSignalError}
                filteredTechnicalContext={filteredTechnicalContext}
                onAddTicker={handleGlobalSearchRequest}
              />
            )}

            <ConsolidatedSocialSection
              posts={credibilityFeed}
              trendingConversations={trendingConversations}
              groups={relevantGroups}
              activeStock={expandedStock}
              feedFilter={feedFilter}
              onFeedFilterChange={setFeedFilter}
              trendingTab={trendingTab}
              setTrendingTab={setTrendingTab}
              signal={activeSignal}
              signalLoading={activeSignalLoading}
              signalError={activeSignalError}
            />

            <footer className="mt-6 sm:mt-8 border-t border-gray-200 dark:border-slate-900/80 pt-3 sm:pt-4 pb-16 sm:pb-4">
              <p className="text-[10px] sm:text-[11px] text-gray-500 dark:text-slate-500 leading-relaxed text-center">
                {t("dashboard.unifiedDashboard.footerDisclaimer")}
              </p>
            </footer>

          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedStockerDashboard;
